import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import { Link, useHistory, useParams } from "react-router-dom";
import { Trans } from "react-i18next";

import Button from "@/components/atoms/Buttons/Button";
import Input from "@/components/atoms/Input";
import useSubmitData from "@/hooks/useSubmitData";
import i18n from "@/translate/i18n";
import Switch from "@/components/atoms/Switch";

import { ErrorText } from "@/components/organisms/UserDataForm/styles";

import { pixKeySchema } from "@/utils/validations";

import { formatDocumentMask } from "@/utils";
import { FakeInput } from "@/components/molecules/Wrappers/FakeInput";
import config from "@/config";

function PaymentDataForm({ data, setData, setError, setIsFetching }) {
  const history = useHistory();
  const { id, token } = useParams();
  const [customerDataValidated, setCustomerDataValidated] = useState(false);
  const [isEditingCbu, setIsEditingCbu] = useState(false);
  const [currentFormValues, setCurrentFormValues] = useState(null);
  const [isValidEmail, setIsValidEmail] = useState(
    Boolean(data?.customer?.email),
  );
  const [emailHasErrors, setEmailHasErrors] = useState(false);

  const initialAcceptedTerms = Boolean(data?.customer?.terms_accepted_at);
  const initialCountryIso =
    data?.customer?.country_iso || data?.customer_country_iso || "";
  const initialDocumentType =
    data?.customer?.document_type || data?.customer_document_types?.[0] || "";
  const initialDocumentNumber = data?.customer?.document_number || "";
  const initialCbu = data?.bank_payment_data?.account_id || "";

  const [documentInstructions, setDocumentInstructions] = useState({
    show: false,
    message:
      initialDocumentNumber === ""
        ? i18n.t("form.documentNumberPresentInfo")
        : i18n.t("form.documentNumberInfo"),
    type: "info",
  });

  const [accountInstructions, setAccountInstructions] = useState({
    show: false,
    message: i18n.t("form.accountNumberInfo"),
    type: "info",
  });
  const { submitData } = useSubmitData(setError, setIsFetching);

  const validateField = (fieldValue, fieldType) => {
    if (!fieldValue || fieldValue.trim() === "") {
      return {
        isValid: false,
        isEmpty: true,
        message: i18n.t(
          `form.invalid${fieldType === "document" ? "Document" : "Account"}`,
        ),
      };
    }
    if (fieldType === "document") {
      const cleanedValue = fieldValue.replace(/\D/g, "");
      const expectedLength = 11;

      const successMessageKey = initialDocumentNumber
        ? "form.documentNumberInfo"
        : "form.documentNumberPresentInfo";

      return {
        isValid: cleanedValue.length === expectedLength,
        isEmpty: false,
        message:
          cleanedValue.length === expectedLength
            ? i18n.t(successMessageKey)
            : i18n.t("form.invalidDocument"),
      };
    }

    const hasAtLeastOneNonSpaceChar = /\S/.test(fieldValue);
    return {
      isValid: hasAtLeastOneNonSpaceChar,
      isEmpty: false,
      message: hasAtLeastOneNonSpaceChar
        ? i18n.t("form.accountNumberInfo")
        : i18n.t("form.invalidAccount"),
    };
  };

  useEffect(() => {
    if (currentFormValues && data?.type === "withdrawal") {
      const isDocumentValid =
        initialDocumentNumber ||
        validateField(currentFormValues.document_number, "document").isValid;
      const isDocumentType = initialDocumentType
        ? Boolean(currentFormValues.document_type || initialDocumentType)
        : true;

      const isAccountValid =
        initialCountryIso === "BR"
          ? true
          : validateField(currentFormValues.account_id, "account").isValid;

      const areTermsAccepted =
        !config.requireTerms || Boolean(currentFormValues.accepted_terms);

      const isValid =
        isDocumentValid && isDocumentType && areTermsAccepted && isAccountValid;
      setCustomerDataValidated(isValid);
    } else {
      setCustomerDataValidated(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentFormValues,
    data?.type,
    initialDocumentType,
    initialDocumentNumber,
  ]);

  const handleFieldFocus = (
    fieldName,
    setFieldError,
    setInstructions,
    options = {},
  ) => {
    const fieldType = fieldName === "document_number" ? "document" : "account";
    const { showInfoOnFocus = true } = options;

    if (showInfoOnFocus) {
      let messageKey = `form.${fieldType}NumberInfo`;

      if (fieldType === "document" && !initialDocumentNumber) {
        messageKey = "form.documentNumberPresentInfo";
      }
      setInstructions({
        show: true,
        message: i18n.t(messageKey),
        type: "info",
      });
    }
    setFieldError(fieldName, "");
  };

  const validEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailFocus = (setFieldError) => {
    setEmailHasErrors(false);
    setFieldError("email", "");
  };

  const handleEmailBlur = (emailAddress, setFieldTouched, setFieldError) => {
    setFieldTouched("email");
    setFieldError("email", "");
    if (validEmail(emailAddress)) {
      setEmailHasErrors(false);
    } else {
      setFieldError("email", "error");
      setEmailHasErrors(true);
    }
  };

  const handleFieldBlur = (
    fieldName,
    fieldValue,
    setFieldTouched,
    setFieldError,
    setInstructions,
    options = {},
  ) => {
    const fieldType = fieldName === "document_number" ? "document" : "account";
    const { showInfoOnValid = true, onValidCallback } = options;

    setFieldTouched(fieldName);
    setFieldError(fieldName, "");

    const validation = validateField(fieldValue, fieldType);

    if (validation.isEmpty) {
      setInstructions({
        show: true,
        message: validation.message,
        type: "error",
      });
      setFieldError(fieldName, validation.message);
    } else if (validation.isValid) {
      if (showInfoOnValid) {
        setInstructions({
          show: true,
          message: validation.message,
          type: "info",
        });
      } else {
        setInstructions({
          show: false,
          message: "",
          type: "info",
        });
      }
      if (onValidCallback) {
        onValidCallback();
      }
    } else {
      setInstructions({
        show: true,
        message: validation.message,
        type: "error",
      });
      setFieldError(fieldName, validation.message);
    }
  };

  const handleFieldChange = (
    fieldName,
    target,
    setFieldTouched,
    setFieldValue,
    setInstructions,
    options = {},
  ) => {
    const { customLogic, clearInstructionsOnChange = false } = options;

    setFieldTouched(fieldName, true);

    if (clearInstructionsOnChange) {
      setInstructions({
        show: false,
        message: "",
        type: "info",
      });
    }

    if (customLogic) {
      customLogic(target, setFieldValue);
    } else {
      setFieldValue(fieldName, target.value);
    }
  };

  return (
    <Formik
      validationSchema={pixKeySchema}
      initialValues={{
        accepted_terms: initialAcceptedTerms,
        merchant_request_amount: data?.merchant_request_amount || "",
        bank_payment_data: data?.bank_payment_data || "",
        document_number: initialDocumentNumber,
        document_type: initialDocumentType,
        country_iso: initialCountryIso,
        email: data?.customer?.email || data?.customer_data?.email || "",
        account_id: initialCbu,
      }}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={({
        accepted_terms,
        country_iso,
        document_type,
        document_number,
        email,
        merchant_request_amount,
        bank_payment_data,
        account_id,
      }) => {
        const cleanedDocumentNum = document_number.replace(/\D/g, "");
        const transactionData = {
          customer: {
            country_iso,
            document_type,
            document_number: cleanedDocumentNum,
            email,
          },
          merchant_request_amount,
          bank_payment_data: {
            ...bank_payment_data,
            account_id,
          },
        };

        setData((state) => ({
          ...state,
          bank_payment_data: {
            ...state.payment_data,
            paymentDataValidated: true,
            account_id,
          },
          accepted_terms,
          customer_data: {
            ...state.customer,
            ...transactionData.customer,
          },
        }));

        if (customerDataValidated) {
          submitData({
            ...data,
            merchant_request_amount,
            bank_payment_data: {
              ...data?.bank_payment_data,
              paymentDataValidated: true,
              account_id,
            },
            accepted_terms,
            customer: {
              ...data.customer,
              ...transactionData.customer,
            },
          });
        } else {
          history.push(`/${id}/${token}${history.location.search}`);
        }
      }}
    >
      {({
        errors,
        setErrors,
        setFieldTouched,
        setFieldError,
        setFieldValue,
        values,
      }) => {
        const newFormValues = {
          document_number: values.document_number,
          document_type: values.document_type,
          accepted_terms: values.accepted_terms,
          account_id: values.account_id,
        };

        if (
          JSON.stringify(currentFormValues) !== JSON.stringify(newFormValues)
        ) {
          setCurrentFormValues(newFormValues);
        }

        return (
          <Form
            onChange={() => setErrors({})}
            className="w-full flex flex-col flex-grow"
          >
            <div className="flex flex-col flex-grow justify-center">
              <div className="flex flex-col justify-between items-center">
                {initialDocumentNumber ? (
                  <>
                    <FakeInput
                      label={i18n.t(
                        `form.document.${values?.document_type || "generic"}`,
                      )}
                      value={formatDocumentMask(
                        values?.document_number,
                        values?.document_type,
                      )}
                      className="mb-0"
                    />
                    <div className="mt-1 mb-5 text-[0.65rem] pl-1 text-left text-dark-gray">
                      {documentInstructions.message}
                    </div>
                  </>
                ) : (
                  <div className="w-full mb-5">
                    <Input
                      id="document_number"
                      name="document_number"
                      type="text"
                      label={i18n.t(
                        `form.document.${values?.document_type || "generic"}`,
                      )}
                      placeholder={i18n.t("form.documentPlaceholder")}
                      value={values?.document_number || ""}
                      error={!!errors?.document_number}
                      handleFocus={() =>
                        handleFieldFocus(
                          "document_number",
                          setFieldError,
                          setDocumentInstructions,
                        )
                      }
                      handleBlur={() =>
                        handleFieldBlur(
                          "document_number",
                          values?.document_number,
                          setFieldTouched,
                          setFieldError,
                          setDocumentInstructions,
                        )
                      }
                      handleChange={({ target }) => {
                        handleFieldChange(
                          "document_number",
                          target,
                          setFieldTouched,
                          setFieldValue,
                          setDocumentInstructions,
                          {
                            customLogic: (inputTarget, setFieldValueFn) => {
                              const documentType =
                                values?.document_type || "cuit-cuil";
                              const type = Array.isArray(documentType)
                                ? documentType[0] || "cuit-cuil"
                                : documentType;
                              setFieldValueFn(
                                "document_number",
                                formatDocumentMask(
                                  inputTarget.value,
                                  String(type).toLowerCase(),
                                ),
                              );
                            },
                          },
                        );
                      }}
                      maxLength={11}
                      disabled={!!initialDocumentNumber}
                    />
                    {documentInstructions.show && (
                      <div
                        className={`mt-1 text-[0.65rem] pl-1 text-left ${
                          documentInstructions.type === "error"
                            ? "text-cripten-red"
                            : "text-dark-gray"
                        }`}
                      >
                        {documentInstructions.message}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="w-full mb-5">
                <Input
                  id="email"
                  name="email"
                  type="text"
                  label={i18n.t("form.email")}
                  placeholder={i18n.t("form.emailPlaceholder")}
                  value={values?.email || ""}
                  error={!!errors?.email}
                  handleFocus={() => handleEmailFocus(setFieldError)}
                  handleChange={({ target }) => {
                    setFieldValue("email", target.value);
                    const isValid = validEmail(target.value);
                    setIsValidEmail(isValid);
                    if (emailHasErrors && target.value.trim() !== "") {
                      setEmailHasErrors(false);
                    }
                  }}
                  handleBlur={() =>
                    handleEmailBlur(
                      values?.email,
                      setFieldTouched,
                      setFieldError,
                    )
                  }
                />
                {emailHasErrors && (
                  <div className="mt-1 text-[0.65rem] text-cripten-red pl-1 text-left">
                    {i18n.t("form.invalidEmail")}
                  </div>
                )}
              </div>

              {initialCountryIso !== "BR" &&
                (!isEditingCbu ? (
                  <FakeInput
                    label={i18n.t("form.document.account_id")}
                    value={values?.account_id}
                    isEditable
                    onClick={() => setIsEditingCbu(true)}
                  />
                ) : (
                  <div className="w-full mb-5">
                    <Input
                      id="account_id"
                      name="account_id"
                      type="text"
                      label={i18n.t("form.document.account_id")}
                      placeholder={i18n.t("form.accountPlaceholder")}
                      autoFocus={isEditingCbu}
                      value={values?.account_id || ""}
                      error={!!errors?.account_id}
                      handleFocus={() =>
                        handleFieldFocus(
                          "account_id",
                          setFieldError,
                          setAccountInstructions,
                          { showInfoOnFocus: false },
                        )
                      }
                      handleBlur={() =>
                        handleFieldBlur(
                          "account_id",
                          values?.account_id,
                          setFieldTouched,
                          setFieldError,
                          setAccountInstructions,
                          {
                            showInfoOnValid: false,
                            onValidCallback: () => setIsEditingCbu(false),
                          },
                        )
                      }
                      handleChange={({ target }) => {
                        handleFieldChange(
                          "account_id",
                          target,
                          setFieldTouched,
                          setFieldValue,
                          setAccountInstructions,
                          {
                            clearInstructionsOnChange: true,
                            customLogic: (inputTarget, setFieldValueFn) => {
                              setFieldValueFn("account_id", inputTarget.value);
                            },
                          },
                        );
                      }}
                      maxLength={22}
                    />
                    {accountInstructions.show && (
                      <div
                        className={`mt-1 text-[0.65rem] pl-1 text-left ${
                          accountInstructions.type === "error"
                            ? "text-cripten-red"
                            : "text-dark-gray"
                        }`}
                      >
                        {accountInstructions.message}
                      </div>
                    )}
                  </div>
                ))}

              {config.requireTerms && !initialAcceptedTerms && (
                <div
                  className="flex mt-5 items-center gap-3 bg-white"
                  data-theme="systemSwitchTheme"
                >
                  <Switch
                    isChecked={values?.accepted_terms}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setFieldTouched("accepted_terms");
                      setFieldValue("accepted_terms", isChecked);

                      if (isChecked) {
                        const documentValidation = validateField(
                          values.document_number,
                          "document",
                        );
                        if (!documentValidation.isValid) {
                          setDocumentInstructions({
                            show: true,
                            message: documentValidation.message,
                            type: "error",
                          });
                          setFieldError(
                            "document_number",
                            documentValidation.message,
                          );
                        }

                        if (initialCountryIso !== "BR") {
                          const accountValidation = validateField(
                            values.account_id,
                            "account",
                          );
                          if (!accountValidation.isValid) {
                            setAccountInstructions({
                              show: true,
                              message: accountValidation.message,
                              type: "error",
                            });
                            setFieldError(
                              "account_id",
                              accountValidation.message,
                            );
                          }
                        }
                      }
                    }}
                  />
                  <p className="m-0 text-left text-sm text-black">
                    <Trans
                      i18nKey="form.terms"
                      i18n={i18n}
                      components={[
                        <Link
                          className="text-blue-600 hover:text-blue-800 font-bold underline"
                          to={{
                            pathname: `/terms-and-conditions/${id}/${token}`,
                            search: history.location.search,
                          }}
                        />,
                        <Link
                          className="text-blue-600 hover:text-blue-800 font-bold underline"
                          to={{
                            pathname: `/privacy-policy/${id}/${token}`,
                            search: history.location.search,
                          }}
                        />,
                      ]}
                    />
                  </p>
                  {errors?.accepted_terms && (
                    <ErrorText>{errors?.accepted_terms}</ErrorText>
                  )}
                </div>
              )}
            </div>
            <Button
              type="submit"
              disabled={!customerDataValidated || !isValidEmail}
            >
              {i18n.t("form.submit.withdrawal")}
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
}

PaymentDataForm.propTypes = {
  setData: PropTypes.func,
  setError: PropTypes.func,
  setIsFetching: PropTypes.func,
  data: PropTypes.shape({
    customer: PropTypes.shape({
      country_iso: PropTypes.string,
      birth_date: PropTypes.string,
      terms_accepted_at: PropTypes.string,
      document_number: PropTypes.string,
      document_type: PropTypes.string,
      email: PropTypes.string,
    }),
    customer_data: PropTypes.shape({
      birth_date: PropTypes.string,
      document_number: PropTypes.string,
      email: PropTypes.string,
      name: PropTypes.string,
    }),
    customer_country_iso: PropTypes.string,
    customer_document_types: PropTypes.arrayOf(PropTypes.string),
    merchant_request_amount: PropTypes.number,
    merchant_request_currency_iso: PropTypes.string,
    type: PropTypes.string,
    bank_payment_data: PropTypes.shape({
      account_id: PropTypes.string,
      allowReview: PropTypes.bool,
    }),
  }),
};

export default PaymentDataForm;
